<template>
  <div class="rm-screen">
    <div v-if="!selectedMealCompanies.length" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-domain</v-icon>
      {{ $t("no_companies_selected") }}
    </div>
    <div v-else-if="loading.fetchDailyMoreRestaurants" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!reservedCompanies.length" class="rm-empty">
      <template v-if="filters.search">{{ $t("report_mobile.no_results_for", { search: filters.search }) }}</template>
      <template v-else>
        <v-icon size="40" color="#8a817a">mdi-food-off</v-icon>
        {{ $t("no_reserved_meals_data") }}
        <span class="rm-muted">{{ $t("select_date_and_company") }}</span>
      </template>
    </div>

    <template v-else>
      <div class="rm-stats rm-stats--pair">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("r_o") }}</span>
          <span class="rm-stat__value rm-num">{{ mealTotal }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("companies") }}</span>
          <span class="rm-stat__value rm-num">{{ reservedCompanies.length }}</span>
        </div>
      </div>

      <section v-for="company in reservedCompanies" :key="company.key" class="rm-card rm-mcard">
        <button
          type="button"
          class="rm-mcard__head"
          :aria-expanded="String(isOpen(company.key))"
          @click="toggle(company.key)"
        >
          <span class="rm-mcard__title">
            <span class="rm-mcard__name">{{ company.name }}</span>
          </span>
          <span class="rm-count-pill" :title="pluralT('report_mobile.meals_n', company.total)">{{ company.total }}</span>
          <v-icon size="18" color="#8a817a" class="rm-mcard__chevron" :class="{ 'is-open': isOpen(company.key) }">
            mdi-chevron-down
          </v-icon>
        </button>

        <ul v-if="isOpen(company.key)" class="rm-list rm-mcard__list">
          <li v-for="food in company.foods" :key="food.key" class="rm-mrow rm-mrow--action">
            <button type="button" class="rm-mrow__btn" @click="openEmployeesDialog(food.source, company.source)">
              <span class="rm-mrow__text">
                <span class="rm-mrow__main">
                  <span class="rm-mrow__name">{{ food.name }}</span>
                  <span class="rm-mrow__value rm-num">{{ food.total }}</span>
                </span>
                <span v-if="food.shifts.length" class="rm-mrow__shifts">
                  <span v-for="shift in food.shifts" :key="shift.id" class="rm-mrow__shift rm-num">
                    <report-shift-tag :shift="shift.id"></report-shift-tag>
                    {{ shift.quantity }}
                  </span>
                </span>
              </span>
              <span class="rm-visually-hidden">{{ $t("show_employees_who_ordered") }}</span>
              <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
            </button>
          </li>
        </ul>
      </section>
    </template>

    <employees-who-ordered-dialog
      v-model="employeesDialog"
      :selected-meal="selectedMeal"
      :selected-company="selectedCompany"
      :filters="filters"
    />
  </div>
</template>

<script>
import ReportMealsTabReservedMeals from "@/components/report/meals/tabs/ReportMealsTabReservedMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import hasPlural from "../../shared/hasPlural";
import ReportShiftTag from "../../shared/ReportShiftTag.vue";
import "./reportMobileMealCards.scss";

/**
 * Mobilni prikaz "Rezerve": po firmi rezervisani obroci sa količinom po smeni; klik na obrok otvara radnike koji
 * su ga naručili. Firme, period i pretraga su u zaglavlju (kao u ostalim izveštajima). Nasleđuje učitavanje i
 * dijalog radnika iz ReportMealsTabReservedMeals.
 */
export default {
  name: "ReportMobileReserved",
  extends: ReportMealsTabReservedMeals,
  mixins: [hasPlural],
  components: { ReportShiftTag },
  data: () => ({
    closed: {},
  }),
  computed: {
    /** Firme sa rezervisanim obrocima: obroci sa količinom po smeni (samo smene sa rezervacijama). */
    reservedCompanies() {
      const search = this.filters.search;
      return this.companyReservedMeals
        .map((company) => {
          const found = search ? this.customSearch(company.foods, [{ value: "name" }], search) : company.foods;
          return {
            key: company.company,
            source: company,
            name: this.toLatin(company.company),
            total: company.foods.reduce((sum, food) => sum + (food.total || 0), 0),
            foods: found.map((food) => ({
              key: `${company.company}-${food.id || food.name}`,
              source: food,
              name: this.toLatin(food.name),
              total: food.total || 0,
              shifts: SHIFTS.map((shift) => ({ id: shift.id, quantity: food[shift.key] || 0 })).filter(
                (shift) => shift.quantity > 0
              ),
            })),
          };
        })
        .filter((company) => company.total > 0 && company.foods.length);
    },
    mealTotal() {
      return this.reservedCompanies.reduce((sum, company) => sum + company.total, 0);
    },
  },
  methods: {
    isOpen(key) {
      return Boolean(this.filters.search) || !this.closed[key];
    },
    toggle(key) {
      this.closed = { ...this.closed, [key]: !this.closed[key] };
    },
  },
};
</script>
